import type { Chart, ChartOptions, TooltipModel } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import commaNumber from 'comma-number';
import React, { useMemo, useRef } from 'react';

import {
  CHART_FREQUENCY,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC_VIEW_TYPE,
} from '@/entities/chart/model/constants';
import useEventPlugin from '@/features/(charts)/chart-renderer/hooks/use-chart-event-plugin';
import { formatDisplayLabel } from '@/features/(charts)/chart-renderer/lib/chart-utils';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import useColor from '@/hooks/domain/useColor';
import { BaseBarChart, useChartTooltip } from '@/shared/ui/charts';
import { getContrastTextColor } from '@/styles/colors/color.utils';

import ChartCustomTooltip from '../chart-custom-tooltip/ChartCustomTooltip';
import useChartTimeSeriesStackedBarData from './useChartTimeSeriesStackedBarData';

type Props = {
  chart: ChartBase & { name: string };
  tableData: ChartTableRes;
  dashboardCardInfo?: DashboardCardInfoT;
  xAxisTickInterval?: number; // x축 tick 표시 간격 (기본값: 1 = 모든 레이블 표시)
  customTooltip?: React.ComponentType<{
    tooltip: TooltipModel<'line'>;
    meta: ChartBase & { name: string };
    tableData: ChartTableRes;
  }>;
  yAxisTitle?: string;
};

type ChartFrequency = (typeof CHART_FREQUENCY)[keyof typeof CHART_FREQUENCY];

// term 값에 따라 적절한 tick interval 계산
const getDefaultTickInterval = (
  frequency: ChartFrequency | null,
  termValue?: number,
): number => {
  if (!frequency || !termValue) return 1;

  switch (frequency) {
    case CHART_FREQUENCY.HOUR:
      // 24시간 이하: 3시간 간격, 72시간 이하: 6시간 간격, 그 이상: 12시간 간격
      if (termValue <= 24) return 3;
      if (termValue <= 72) return 6;
      return 12;
    case CHART_FREQUENCY.DAY:
      // 7일 이하: 모든 레이블, 30일 이하: 7일 간격
      if (termValue <= 7) return 1;
      if (termValue <= 30) return 7;
      return 14;
    default:
      return 1;
  }
};

const TimeSeriesStackedBarChart = (props: Props) => {
  const color = useColor();
  const chartRef = useRef<Chart<'bar'>>(null);

  const barChartData = useChartTimeSeriesStackedBarData(props);
  const { tooltipData, chartTooltip, hideTooltip } = useChartTooltip({
    chart: chartRef.current,
  });
  const eventPlugin = useEventPlugin({
    meta: props.chart,
    hideTooltip,
    dashboardCardInfo: props.dashboardCardInfo,
  });

  const isPercent =
    props.chart.series[0]?.options?.view_type ===
    CHART_METRIC_VIEW_TYPE.PERCENTAGE;

  const isVertical =
    props.chart.graph_direction === CHART_GRAPH_DIRECTION.VERTICAL;

  const barChartOptions: ChartOptions<'bar'> = useMemo(() => {
    return {
      animation: false,
      indexAxis: isVertical ? 'x' : 'y',
      interaction: {
        mode: 'nearest',
        intersect: true,
      },

      scales: {
        [isVertical ? 'y' : 'x']: {
          grid: {
            color: 'transparent',
          },
          stacked: true,
          min: props.chart.series[0]?.options?.scale_min || 0,
          max:
            props.chart.series[0]?.options?.scale_max || isPercent
              ? 100
              : undefined,
          ticks: {
            callback(value) {
              if (isPercent) return `${value}%`;
              return value;
            },
          },
          title: {
            display: !!props.yAxisTitle,
            text: props.yAxisTitle || '',
          },
        },
        [isVertical ? 'x' : 'y']: {
          stacked: true,
          ticks: {
            callback: function (value, index) {
              const originalLabel = this.getLabelForValue(value as number);
              // interval 계산 (props 우선, 없으면 자동 계산)
              const tickInterval =
                props.xAxisTickInterval ??
                getDefaultTickInterval(
                  props.chart.historical.frequency,
                  props.chart.target.term.value,
                );
              if (tickInterval > 1 && index % tickInterval !== 0) return '';
              // context-aware 포맷 적용
              const frequency = props.chart.historical.frequency;
              if (!frequency) return originalLabel;
              const prevVisibleIndex = index - tickInterval;
              const prevLabel =
                prevVisibleIndex >= 0
                  ? this.getLabelForValue(prevVisibleIndex)
                  : null;
              return formatDisplayLabel(originalLabel, frequency, prevLabel);
            },
          },
        },
      },
      plugins: {
        eventPlugin: {
          events: ['mouseleave', 'click'],
        },
        tooltip: {
          enabled: false,
          external: chartTooltip,
        },
        legend: {
          labels: {
            color: color.gray700,
          },
          position: 'right',
        },
        datalabels: {
          align: 'center',
          anchor: 'center',
          display: 'auto',
          color: (context) => {
            const backgroundColor = context.dataset.backgroundColor;
            const segmentColor = Array.isArray(backgroundColor)
              ? backgroundColor[context.dataIndex]
              : backgroundColor;

            return getContrastTextColor(
              typeof segmentColor === 'string' ? segmentColor : '#FFFFFF',
            );
          },
          formatter: (value) => {
            if (Number(value) === 0) return null;

            const formattedValue = commaNumber(Number(value));
            return isPercent ? `${formattedValue}%` : formattedValue;
          },
        },
      },
    };
  }, [
    chartTooltip,
    color.gray700,
    isPercent,
    isVertical,
    props.chart.series,
    props.xAxisTickInterval,
    props.chart.historical.frequency,
    props.chart.target.term.value,
  ]);

  return (
    <React.Fragment>
      <BaseBarChart
        chartRef={chartRef}
        data={barChartData}
        options={barChartOptions}
        plugins={[eventPlugin, ChartDataLabels]}
      />
      {tooltipData &&
        (props.customTooltip ? (
          <props.customTooltip
            tooltip={tooltipData}
            meta={props.chart}
            tableData={props.tableData}
          />
        ) : (
          <ChartCustomTooltip
            tooltip={tooltipData}
            meta={props.chart}
            tableData={props.tableData}
            dashboardCardInfo={props.dashboardCardInfo}
          />
        ))}
    </React.Fragment>
  );
};

export default TimeSeriesStackedBarChart;
